import * as React from 'react'

import { useReducedMotion } from 'motion/react'
import * as m from 'motion/react-m'

import type { EngagementKind } from '../../hooks/engagement/newEngagementSignal'

import styles from './NewItemHighlight.module.css'

import type { JSX } from 'react'

/**
 * Items that have already played their highlight this session, keyed by inbox
 * and id.
 *
 * An item stays flagged as newly arrived for as long as its divider is on
 * screen, so "has this already flashed?" can't live in component state: the
 * card remounts on the next refetch and would flash a second time. Keying it by
 * item means each arrival is announced exactly once, however often its card is
 * rebuilt.
 */
const flashedItems = new Set<string>()

const flashKey = (kind: EngagementKind, itemId: string): string =>
  `${kind}:${itemId}`

/** Forget which items have flashed, so they would announce themselves again. */
export const clearFlashedNewItems = (): void => flashedItems.clear()

type NewItemHighlightProps = {
  /** Which inbox the item belongs to, so the two never share a key. */
  kind: EngagementKind
  /** The comment or mention this highlight belongs to. */
  itemId: string
}

/**
 * A brand-tinted panel behind a card's content that fades out once, marking a
 * comment or mention the user hasn't caught up with. It sits behind the
 * (transparent) card background so the card's own hover background is never
 * overridden, and unmounts when the fade completes. Skipped entirely under
 * reduced-motion.
 *
 * The card that renders this must be `position: relative` so the panel anchors
 * to it.
 */
export const NewItemHighlight = ({
  kind,
  itemId,
}: NewItemHighlightProps): JSX.Element | null => {
  const shouldReduceMotion = useReducedMotion()
  const [visible, setVisible] = React.useState(
    () => !shouldReduceMotion && !flashedItems.has(flashKey(kind, itemId)),
  )

  // Flag it as soon as it starts playing, so a remount midway through doesn't
  // restart the fade.
  React.useEffect(() => {
    if (visible) flashedItems.add(flashKey(kind, itemId))
  }, [visible, kind, itemId])

  if (!visible) return null

  return (
    <m.span
      aria-hidden
      className={styles.highlight}
      initial={{ opacity: 1 }}
      animate={{ opacity: 0 }}
      // Hold the tint fully visible for a beat so the arrival registers, then
      // fade it out.
      transition={{ delay: 0.9, duration: 1.4, ease: [0.15, 0, 0.35, 1] }}
      onAnimationComplete={(): void => setVisible(false)}
    />
  )
}
